import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { whatsappGreetingQueryOptions } from "@/lib/whatsapp-greeting";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

function NotFoundComponent() {
  const suggestions = [
    { to: "/", label: "Home" },
    { to: "/benagil-cave-tour", label: "Benagil Cave Tour" },
    { to: "/sunset-cruise-vilamoura", label: "Sunset Cruise" },
    { to: "/private-yacht-charter-vilamoura", label: "Private Yacht Charter" },
    { to: "/vilamoura-boat-trips", label: "Vilamoura Boat Trips" },
  ] as const;
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-xl text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved. Try one of our cruises:
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {suggestions.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="inline-flex items-center justify-center border border-input bg-background px-3 py-1.5 text-xs uppercase tracking-widest text-foreground transition-colors hover:bg-accent"
            >
              {s.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(whatsappGreetingQueryOptions);
    return null;
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Morningstar Yacht · Vilamoura" },
      { name: "description", content: "Private yacht charters from Marina de Vilamoura aboard Morningstar." },
      { property: "og:site_name", content: "Morningstar Yacht" },
      { property: "og:type", content: "website" },
      { name: "theme-color", content: "#0d3a30" },
      { property: "og:title", content: "Morningstar Yacht · Vilamoura" },
      { name: "twitter:title", content: "Morningstar Yacht · Vilamoura" },
      { property: "og:description", content: "Private yacht charters from Marina de Vilamoura aboard Morningstar." },
      { name: "twitter:description", content: "Private yacht charters from Marina de Vilamoura aboard Morningstar." },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      { rel: "shortcut icon", href: "/favicon.ico", type: "image/x-icon" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preload", as: "image", href: "/__l5e/assets-v1/b243c9a3-02a3-4fe6-87ca-d4f8b4c65385/hero-poster.avif", type: "image/avif", fetchpriority: "high" },
      // Non-blocking fonts: load as print stylesheet, swap to all after load (script below)
      ({
        rel: "stylesheet",
        id: "msy-fonts",
        media: "print",
        href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;1,400&family=Inter:wght@400;600;700&display=swap",
      } as any),
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    scripts: [
      // Swap fonts media to "all" once the stylesheet finishes loading (non-blocking)
      {
        children:
          "(function(){var l=document.getElementById('msy-fonts');if(!l)return;function swap(){l.media='all';}l.addEventListener('load',swap,{once:true});if(l.sheet){swap();}})();",
      },
      // Google Consent Mode v2 default — tiny, must run before any tag
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('consent','default',{'analytics_storage':'denied','ad_storage':'denied','ad_user_data':'denied','ad_personalization':'denied','wait_for_update':500});try{var c=localStorage.getItem('msy_cookie_consent');if(c==='accepted'){gtag('consent','update',{'analytics_storage':'granted','ad_storage':'granted','ad_user_data':'granted','ad_personalization':'granted'});}}catch(e){}",
      },
      // Deferred tracking loader — fires GTM (+ GA4 / Meta Pixel / Clarity via GTM tags)
      // on first user interaction (pointerdown/keydown/touchstart/scroll) or 3s after load,
      // whichever comes first. Exposes window.__msyLoadTracking() so CookieConsent can fire it
      // immediately when the user clicks "Accept".
      {
        children:
          "(function(){var loaded=false;function load(){if(loaded)return;loaded=true;window.__msyLoadTracking=function(){};remove();window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtm.js?id=GTM-M7PSF467';document.head.appendChild(s);var g=document.createElement('script');g.async=true;g.src='https://www.googletagmanager.com/gtag/js?id=G-TDV1RHX5RQ';document.head.appendChild(g);function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-TDV1RHX5RQ');}var events=['pointerdown','keydown','touchstart','scroll','mousemove'];var opts={once:true,passive:true,capture:true};function remove(){events.forEach(function(e){window.removeEventListener(e,load,opts);});}events.forEach(function(e){window.addEventListener(e,load,opts);});window.__msyLoadTracking=load;function armFallback(){setTimeout(load,4000);}if(document.readyState==='complete'){armFallback();}else{window.addEventListener('load',armFallback,{once:true});}})();",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Morningstar Yacht",
          description: "Private yacht charters from Marina de Vilamoura, Algarve.",
          areaServed: "Algarve, Portugal",
          address: { "@type": "PostalAddress", addressLocality: "Vilamoura", addressRegion: "Algarve", addressCountry: "PT" },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-M7PSF467"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}
